// Public render types: the contract the game session codes against (docs/INTERNALS.md "Client module
// interfaces"). Pure type declarations plus the PlayerFlags bit constants (gr_core::protocol::PlayerFlags).

import type { ProjectileSnapData } from "../wasm";
import type { ShellKind } from "./weaponFx";

export type Vec3Like = { x: number; y: number; z: number };

/** Bits of `RemotePlayerRenderState.flags` / `InterpolatedPlayer.flags` (gr_core PlayerFlags, verified). */
export const PlayerFlag = {
  ALIVE: 1 << 0,
  ON_GROUND: 1 << 1,
  CROUCHED: 1 << 2,
  SLIDING: 1 << 3,
  WALLRUN_LEFT: 1 << 4,
  WALLRUN_RIGHT: 1 << 5,
  MANTLING: 1 << 6,
  ADS: 1 << 7,
  RELOADING: 1 << 8,
  WALKING: 1 << 9,
  PLANTING: 1 << 10,
  DEFUSING: 1 << 11,
  HAS_BOMB: 1 << 12,
  SPAWN_PROTECTED: 1 << 13,
  FLASHED: 1 << 14,
} as const;

/**
 * Per-frame viewmodel input. Angles are degrees; `lookDeltaX/Y` is the view change of this frame as
 * the mouse moved it (+X = looked right, +Y = looked up). `kickPitchDeg/kickYawDeg` is the camera's
 * current recoil offset (+pitch up, +yaw right). `meleeFrac` / `throwFrac` are 0 when idle, else
 * progress in (0, 1]. `reloadFrac` / `switchFrac` are -1 when idle.
 */
export interface ViewmodelState {
  weaponId: string;
  attachments: string[];
  camo: string;
  visible: boolean;
  adsFrac: number;
  reloadFrac: number;
  switchFrac: number;
  shotsThisFrame: number;
  /** Largest view kick (degrees up) among the shots of this frame; heavier guns throw the model harder. */
  shotKickDeg: number;
  kickPitchDeg: number;
  kickYawDeg: number;
  lookDeltaX: number;
  lookDeltaY: number;
  speed: number;
  onGround: boolean;
  sliding: boolean;
  wallrunSide: -1 | 0 | 1;
  meleeFrac: number;
  throwFrac: number;
}

export interface ViewmodelRenderer {
  /** Optional pre-build (instant first raise); `update` also rebuilds when the loadout fields change. */
  setLoadoutWeapon(id: string, attachments: string[], camo: string): void;
  update(s: ViewmodelState, dt: number): void;
  /** World position of the visible muzzle (corrected for the different viewmodel FOV). */
  muzzleWorldPosition(out?: Vec3Like): Vec3Like;
  /** True at full ADS with a scoped weapon (the viewmodel is hidden; the HUD draws the overlay). */
  scopeActive(): boolean;
}

export interface RemotePlayerInfo {
  id: number;
  name: string;
  team: "a" | "b" | "none";
  enemy: boolean;
  weaponId: string;
  attachments: string[];
  camo: string;
}

/** `yaw` / `pitch` in radians, `pos` = feet position. */
export interface RemotePlayerRenderState {
  pos: Vec3Like;
  vel: Vec3Like;
  yaw: number;
  pitch: number;
  flags: number;
  weaponId: string;
  firing: boolean;
  /** Seconds since death, 0 when alive. */
  deathTime: number;
  /** Not drawn: the spectator camera follows this player too closely to show the body. */
  hidden?: boolean;
}

export interface PlayerRenderer {
  upsert(info: RemotePlayerInfo): void;
  setState(id: number, s: RemotePlayerRenderState, dt: number): void;
  remove(id: number): void;
  /** Point above the head for nameplates; null for unknown, dead or hidden players. The object is reused. */
  headPosition(id: number): Vec3Like | null;
  clear(): void;
}

export interface EffectsRenderer {
  tracer(from: Vec3Like, to: Vec3Like, local: boolean): void;
  /** `power`: flash scale of the weapon class (1 = assault rifle). */
  muzzleFlash(
    pos: Vec3Like,
    dir: Vec3Like,
    local: boolean,
    power?: number,
  ): void;
  /** A case leaves the ejection port of the first-person gun (`delaySec` after the shot). */
  ejectShell(kind: ShellKind, delaySec: number): void;
  /** `eyeDist`: distance from the shooter's eye, so close hits stay small and never hide the crosshair. */
  impact(pos: Vec3Like, normal: Vec3Like, eyeDist?: number): void;
  hitSpark(pos: Vec3Like): void;
  /** `kind`: equipment id ("frag", "flash", "smoke", "impulse") or "bomb". */
  explosion(pos: Vec3Like, kind: string): void;
  /**
   * A glass pane (box `min`..`max`) bursting into shards thrown from `at` along `push` (m/s) that fall
   * to `floorY`; the bullet holes on the pane go with it.
   */
  shatter(
    min: Vec3Like,
    max: Vec3Like,
    at: Vec3Like,
    push: Vec3Like,
    floorY: number,
  ): void;
  smoke(key: number, pos: Vec3Like, radius: number, remainingSec: number): void;
  /** White-out overlay level 0..1 (decays on its own when not refreshed every frame). */
  flash(intensity: number): void;
  clear(): void;
}

export interface ProjectileRenderer {
  /** `ageSec`: how long ago the snapshot of `list` happened on the shown timeline. */
  sync(list: ProjectileSnapData[], ageSec?: number): void;
}

export interface ObjectiveRenderer {
  setHill(
    index: number | null,
    owner: "a" | "b" | "none",
    contested: boolean,
  ): void;
  /** The hill that scores next, outlined while it is announced (the last seconds of the hill in play); null hides it. */
  setHillPreview(index: number | null): void;
  setBomb(state: { pos: Vec3Like; planted: boolean } | null): void;
  /** Bomb site markers (Defusal only; hidden by default). */
  setBombSitesVisible(visible: boolean): void;
  clear(): void;
}
